/**
 * Réseau du domaine skiable : comment pistes, remontées et village se
 * relient, et où un skieur peut aller.
 *
 * Partagé : le serveur s'en sert pour les décisions des skieurs (quelle
 * remontée, quelle bifurcation), le client pour l'aperçu d'un tracé (à quoi
 * se raccordent son départ et son arrivée), la fiche des pistes et le plan.
 *
 * Nœuds (`NodeKey`) :
 * - "V" : village, au bas du domaine ;
 * - "B<id>" / "T<id>" : gare aval / sortie amont d'une remontée ;
 * - "P<id>@<s>" : point de la piste <id> à s mètres de son départ, mesurés le
 *   long de sa trajectoire. Sert aux raccords (une piste part d'une autre ou
 *   la rejoint) et aux extrémités qui ne rejoignent rien.
 *
 * Les extrémités d'une piste ne sont pas des liens stockés : elles se
 * déduisent de la géométrie (gare, piste ou village à proximité). Démolir une
 * remontée ou une piste ne laisse donc jamais de lien vers un objet disparu ;
 * une piste dont l'arrivée ne rejoint plus rien devient un cul-de-sac signalé.
 *
 * Une remontée peut aussi être posée au milieu d'une piste : sa gare aval
 * devient un arrêt (on quitte la piste pour remonter, ou on poursuit la
 * descente) et son sommet, un accès (on rejoint la piste en cours de route).
 */
import { LIFTS, type LiftKind } from './catalog';
import { distPointPolyline, type Vec2 } from './geometry';
import { computeLiftGeometry, queueSlot, topExit, type LiftGeometry } from './lifts';
import type { GameMap } from './map';
import { WALK_SPEED } from './motion';
import { makePisteTrack, pisteSuitability, type PisteTrack } from './pistes';
import type { Difficulty, EntityId, LiftStatus, NodeKey } from './state';
import type { Heightfield } from './terrain';

/** Estimation du trajet de retour au village : vitesse à ski (m/s), attente à une remontée (s). */
const HOME = { skiSpeed: 8, liftWait: 120 } as const;

/** Distances d'accroche et de marche (m). */
export const NET = {
  /** Accroche à la sortie d'une gare amont. */
  topRadius: 45,
  /** Accroche à une gare aval (entrée de la file). */
  bottomRadius: 55,
  /** Accroche à une piste : au-delà de sa demi-largeur. */
  pisteMargin: 6,
  /** Un raccord à moins de cette distance d'une extrémité se fait sur l'extrémité. */
  endSnap: 25,
  /** Gare posée sur une piste ou à son bord : distance au-delà de la demi-largeur. */
  stationMargin: 30,
  /** Marche jusqu'à une gare aval, et depuis une sortie amont (enchaînement). */
  walk: 330,
  walkFromTop: 150,
  /** Dénivelé maximal gravi à pied. */
  walkClimb: 40,
} as const;

/** Rang de difficulté (0 verte ... 3 noire). */
export const DIFFICULTY_RANK: Record<Difficulty, number> = { verte: 0, bleue: 1, rouge: 2, noire: 3 };

/** Niveaux de skieurs et difficulté la plus forte qu'ils empruntent. */
export const SKILL_LEVELS = [
  { skill: 0, label: 'Débutants', max: 1, hint: 'vertes et bleues' },
  { skill: 1, label: 'Intermédiaires', max: 2, hint: "jusqu'aux rouges" },
  { skill: 2, label: 'Experts', max: 3, hint: 'toutes les pistes' },
] as const;

export interface NetLift {
  id: EntityId;
  name: string;
  kind: LiftKind;
  bottom: Vec2;
  top: Vec2;
  status?: LiftStatus;
}

export interface NetPiste {
  id: EntityId;
  name: string;
  difficulty: Difficulty;
  path: Vec2[];
  width: number;
  open?: boolean;
}

export interface NetworkSource {
  hf: Heightfield;
  map: GameMap;
  lifts: Iterable<NetLift>;
  pistes: Iterable<NetPiste>;
  /** Géométrie déjà calculée par l'appelant (cache), sinon recalculée. */
  liftGeo?: (l: NetLift) => LiftGeometry;
  /** Trajectoire déjà calculée par l'appelant (cache), sinon recalculée. */
  track?: (p: NetPiste) => PisteTrack;
}

export type AnchorKind = 'top' | 'bottom' | 'village' | 'piste';

/** Ce à quoi se raccorde une extrémité de piste. */
export interface Anchor {
  kind: AnchorKind;
  node: NodeKey;
  /** Point d'accroche : sortie de gare, file d'attente, point de la piste, village. */
  point: Vec2;
  liftId?: EntityId;
  pisteId?: EntityId;
  /** Abscisse sur la piste rejointe (m). */
  s?: number;
  label: string;
}

export interface NetNode {
  key: NodeKey;
  pos: Vec2;
  label: string;
}

/** Remontée posée sur une piste, à l'abscisse s : sa gare aval (arrêt) ou son sommet (accès). */
export interface NetStation {
  s: number;
  node: NodeKey;
  liftId: EntityId;
}

export interface NetPisteInfo {
  piste: NetPiste;
  track: PisteTrack;
  /** Longueur de la trajectoire (m), base des abscisses des raccords. */
  length: number;
  from: NodeKey;
  to: NodeKey;
  /** Abscisses où d'autres pistes partent de celle-ci (bifurcations). */
  branches: number[];
  /** Abscisses où d'autres pistes la rejoignent. */
  merges: number[];
  /** Gares aval posées le long de la piste : on peut s'y arrêter pour remonter. */
  stops: NetStation[];
  /** Sommets posés le long de la piste : on la rejoint en cours de route. */
  entries: NetStation[];
}

/** Descente possible depuis un nœud : la piste, prise à l'abscisse s0. */
export interface SkiOption {
  piste: EntityId;
  s0: number;
}

/** Point où l'on peut quitter une piste : bifurcation ou arrivée. */
export interface SkiExit {
  s: number;
  node: NodeKey;
}

export interface ReachOptions {
  /** Niveau du skieur : seules les pistes de sa difficulté maximale comptent. */
  skill?: 0 | 1 | 2;
  /** Pistes ouvertes seulement (sinon tout le tracé construit). */
  openOnly?: boolean;
  /** Prendre les remontées (vrai par défaut). */
  lifts?: boolean;
  /** Liaisons à pied vers les gares et le village (vrai par défaut). */
  walk?: boolean;
}

export interface Reach {
  nodes: Set<NodeKey>;
  /** Pistes atteintes : abscisse la plus haute d'où on les prend (0 = tout le tracé). */
  pistes: Map<EntityId, number>;
  lifts: Set<EntityId>;
}

export type NetworkIssueKind = 'dead-end' | 'isolated-start' | 'no-descent' | 'unreachable' | 'experts-only';

export interface NetworkIssue {
  kind: NetworkIssueKind;
  level: 'warn' | 'info';
  pisteId?: EntityId;
  liftId?: EntityId;
  title: string;
  message: string;
}

const r0 = (v: number) => Math.round(v);

export function pisteNodeKey(pisteId: EntityId, s: number): NodeKey {
  return `P${pisteId}@${r0(s)}`;
}

/** Décompose une clé "P<id>@<s>". */
export function parsePisteNode(key: NodeKey): { piste: EntityId; s: number } | null {
  if (key[0] !== 'P') return null;
  const at = key.indexOf('@');
  if (at < 0) return null;
  const piste = Number(key.slice(1, at));
  const s = Number(key.slice(at + 1));
  return Number.isFinite(piste) && Number.isFinite(s) ? { piste, s } : null;
}

/** Point le plus proche d'une trajectoire : abscisse, distance horizontale et position. */
export function projectOnTrack(track: PisteTrack, p: Vec2): { s: number; dist: number; point: Vec2 } {
  const { xs, zs, cum } = track.path;
  let best = { s: 0, dist: Infinity, point: [xs[0] as number, zs[0] as number] as Vec2 };
  for (let i = 0; i < xs.length - 1; i++) {
    const ax = xs[i] as number;
    const az = zs[i] as number;
    const dx = (xs[i + 1] as number) - ax;
    const dz = (zs[i + 1] as number) - az;
    const l2 = dx * dx + dz * dz;
    let t = l2 > 0 ? ((p[0] - ax) * dx + (p[1] - az) * dz) / l2 : 0;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const x = ax + dx * t;
    const z = az + dz * t;
    const d = Math.hypot(p[0] - x, p[1] - z);
    if (d < best.dist) {
      const s0 = cum[i] as number;
      best = { s: s0 + ((cum[i + 1] as number) - s0) * t, dist: d, point: [x, z] };
    }
  }
  return best;
}

/** Position au sol d'une abscisse de trajectoire. */
export function trackPoint(track: PisteTrack, s: number): Vec2 {
  const out = [0, 0, 0, 0, 0];
  track.path.sample(Math.max(0, Math.min(track.length, s)), out);
  return [out[0] as number, out[2] as number];
}

export class PisteNetwork {
  readonly hf: Heightfield;
  readonly map: GameMap;
  readonly lifts = new Map<EntityId, { lift: NetLift; geo: LiftGeometry; exit: Vec2; entry: Vec2 }>();
  readonly pistes = new Map<EntityId, NetPisteInfo>();
  readonly nodes = new Map<NodeKey, NetNode>();
  private readonly byNode = new Map<NodeKey, SkiOption[]>();
  private readonly alias = new Map<NodeKey, NodeKey>();
  private readonly valueCache = new Map<string, Map<NodeKey, number>>();

  constructor(src: NetworkSource) {
    this.hf = src.hf;
    this.map = src.map;
    for (const l of src.lifts) {
      const geo = src.liftGeo ? src.liftGeo(l) : computeLiftGeometry(src.hf, l.kind, l.bottom, l.top);
      this.lifts.set(l.id, { lift: l, geo, exit: topExit(geo), entry: queueSlot(geo, 0) });
    }
    const raw = new Map<EntityId, { from: NodeKey; to: NodeKey }>();
    for (const p of src.pistes) {
      if (p.path.length < 2) continue;
      const track = src.track ? src.track(p) : makePisteTrack(src.hf, p.id, p.path, p.width);
      this.pistes.set(p.id, { piste: p, track, length: track.length, from: '', to: '', branches: [], merges: [], stops: [], entries: [] });
    }
    // Extrémités brutes, puis alias : le départ et l'arrivée d'une piste valent le nœud qu'ils rejoignent.
    for (const info of this.pistes.values()) {
      const p = info.piste;
      const from = this.anchorAt(p.path[0] as Vec2, 'start', p.id)?.node ?? pisteNodeKey(p.id, 0);
      const to = this.anchorAt(p.path[p.path.length - 1] as Vec2, 'end', p.id)?.node ?? pisteNodeKey(p.id, info.length);
      raw.set(p.id, { from, to });
      const startKey = pisteNodeKey(p.id, 0);
      const endKey = pisteNodeKey(p.id, info.length);
      if (from !== startKey) this.alias.set(startKey, from);
      if (to !== endKey) this.alias.set(endKey, to);
    }
    for (const info of this.pistes.values()) {
      const r = raw.get(info.piste.id) as { from: NodeKey; to: NodeKey };
      info.from = this.canonical(r.from);
      info.to = this.canonical(r.to);
    }
    // Raccords internes : bifurcations (une piste part d'ici) et confluences (une piste arrive ici).
    for (const info of this.pistes.values()) {
      for (const [key, list] of [
        [info.from, 'branches'],
        [info.to, 'merges'],
      ] as const) {
        const at = parsePisteNode(key);
        if (!at || at.piste === info.piste.id) continue;
        const host = this.pistes.get(at.piste);
        if (!host || at.s <= 0 || at.s >= r0(host.length)) continue;
        if (!host[list].includes(at.s)) host[list].push(at.s);
      }
    }
    for (const info of this.pistes.values()) {
      info.branches.sort((a, b) => a - b);
      info.merges.sort((a, b) => a - b);
      this.addOption(info.from, { piste: info.piste.id, s0: 0 });
      for (const s of new Set([...info.branches, ...info.merges])) this.addOption(pisteNodeKey(info.piste.id, s), { piste: info.piste.id, s0: s });
    }
    // Remontées posées au milieu d'une piste : arrêt à la gare aval, accès au sommet.
    for (const { lift, exit, entry } of this.lifts.values()) {
      const bottom = `B${lift.id}`;
      const top = `T${lift.id}`;
      for (const info of this.pistes.values()) {
        const reach = info.piste.width / 2 + NET.stationMargin;
        const inside = (s: number) => s > NET.endSnap && s < info.length - NET.endSnap;
        if (info.from !== bottom && info.to !== bottom) {
          const pr = projectOnTrack(info.track, entry);
          if (pr.dist <= reach && inside(pr.s)) {
            const s = r0(pr.s);
            info.stops.push({ s, node: bottom, liftId: lift.id });
            this.addOption(bottom, { piste: info.piste.id, s0: s });
          }
        }
        if (info.from !== top) {
          const pr = projectOnTrack(info.track, exit);
          if (pr.dist <= reach && inside(pr.s)) {
            const s = r0(pr.s);
            info.entries.push({ s, node: top, liftId: lift.id });
            this.addOption(top, { piste: info.piste.id, s0: s });
          }
        }
      }
    }
    for (const info of this.pistes.values()) {
      info.stops.sort((a, b) => a.s - b.s);
      info.entries.sort((a, b) => a.s - b.s);
    }
    // Nœuds nommés et positionnés.
    const v = this.map.features.village;
    this.nodes.set('V', { key: 'V', pos: [v.x, v.z], label: 'Village' });
    for (const { lift, exit, entry } of this.lifts.values()) {
      this.nodes.set(`B${lift.id}`, { key: `B${lift.id}`, pos: entry, label: `Gare aval · ${lift.name}` });
      this.nodes.set(`T${lift.id}`, { key: `T${lift.id}`, pos: exit, label: `Sommet · ${lift.name}` });
    }
    for (const info of this.pistes.values()) {
      for (const key of [info.from, info.to, ...info.branches.map((s) => pisteNodeKey(info.piste.id, s)), ...info.merges.map((s) => pisteNodeKey(info.piste.id, s))]) {
        if (this.nodes.has(key)) continue;
        const at = parsePisteNode(key);
        const host = at ? this.pistes.get(at.piste) : undefined;
        if (!at || !host) continue;
        this.nodes.set(key, { key, pos: trackPoint(host.track, at.s), label: `Piste ${host.piste.name}` });
      }
    }
  }

  private addOption(node: NodeKey, o: SkiOption): void {
    const list = this.byNode.get(node);
    if (list) list.push(o);
    else this.byNode.set(node, [o]);
  }

  /** Clé canonique : une extrémité de piste raccordée vaut le nœud qu'elle rejoint. */
  canonical(key: NodeKey): NodeKey {
    let k = key;
    for (let i = 0; i < 8; i++) {
      const next = this.alias.get(k);
      if (!next || next === k) break;
      k = next;
    }
    return k;
  }

  /**
   * Ce à quoi se raccorderait une extrémité placée en p : gare (prioritaire),
   * piste existante ou village. `role` : départ (on y arrive pour descendre)
   * ou arrivée (on y finit la descente). `exclude` : piste en cours d'édition.
   */
  anchorAt(p: Vec2, role: 'start' | 'end', exclude?: EntityId): Anchor | null {
    // Gares : la plus proche dans le rayon d'accroche.
    let best: Anchor | null = null;
    let bestD = Infinity;
    for (const { lift, exit, entry } of this.lifts.values()) {
      const dTop = Math.hypot(p[0] - exit[0], p[1] - exit[1]);
      const dBottom = Math.hypot(p[0] - entry[0], p[1] - entry[1]);
      if (role === 'start') {
        if (dTop < NET.topRadius && dTop < bestD) {
          bestD = dTop;
          best = { kind: 'top', node: `T${lift.id}`, point: exit, liftId: lift.id, label: `Sommet du ${lift.name}` };
        }
        if (dBottom < NET.topRadius && dBottom < bestD) {
          bestD = dBottom;
          best = { kind: 'bottom', node: `B${lift.id}`, point: entry, liftId: lift.id, label: `Gare aval du ${lift.name}` };
        }
      } else {
        if (dBottom < NET.bottomRadius && dBottom < bestD) {
          bestD = dBottom;
          best = { kind: 'bottom', node: `B${lift.id}`, point: entry, liftId: lift.id, label: `Gare aval du ${lift.name}` };
        }
      }
    }
    if (best) return best;
    // Pistes : point le plus proche dans l'emprise (raccord), extrémités aimantées.
    let pick: { info: NetPisteInfo; s: number; point: Vec2; d: number } | null = null;
    for (const info of this.pistes.values()) {
      if (info.piste.id === exclude) continue;
      const pr = projectOnTrack(info.track, p);
      if (pr.dist > info.piste.width / 2 + NET.pisteMargin) continue;
      if (!pick || pr.dist < pick.d) pick = { info, s: pr.s, point: pr.point, d: pr.dist };
    }
    if (pick) {
      const { info } = pick;
      const len = info.length;
      let s = r0(pick.s);
      if (s < NET.endSnap) s = 0;
      else if (s > len - NET.endSnap) s = r0(len);
      // Une arrivée sur le bas d'une piste équivaut à son arrivée ; un départ en haut, à son départ.
      const key = pisteNodeKey(info.piste.id, s);
      const node = this.canonical(s === 0 ? pisteNodeKey(info.piste.id, 0) : s >= r0(len) ? pisteNodeKey(info.piste.id, len) : key);
      const point = s === 0 ? (info.piste.path[0] as Vec2) : s >= r0(len) ? (info.piste.path[info.piste.path.length - 1] as Vec2) : trackPoint(info.track, s);
      return { kind: 'piste', node, point, pisteId: info.piste.id, s, label: `Piste ${info.piste.name}` };
    }
    if (role === 'end') {
      const v = this.map.features.village;
      if (Math.hypot(p[0] - v.x, p[1] - v.z) < v.radius) return { kind: 'village', node: 'V', point: p, label: 'Village' };
    }
    return null;
  }

  /** Descentes possibles depuis un nœud. */
  options(node: NodeKey): SkiOption[] {
    return this.byNode.get(this.canonical(node)) ?? [];
  }

  /** Bifurcations et gares aval après s0, puis arrivée : les endroits où l'on peut quitter la piste. */
  exits(pisteId: EntityId, s0: number): SkiExit[] {
    const info = this.pistes.get(pisteId);
    if (!info) return [];
    const out: SkiExit[] = [];
    for (const s of info.branches) if (s > s0 + 0.5) out.push({ s, node: pisteNodeKey(pisteId, s) });
    for (const st of info.stops) if (st.s > s0 + 0.5) out.push({ s: st.s, node: st.node });
    out.sort((a, b) => a.s - b.s);
    out.push({ s: info.length, node: info.to });
    return out;
  }

  /** Nœud où l'on se trouve à l'abscisse s d'une piste : gare aval, raccord ou arrivée. */
  nodeAt(pisteId: EntityId, s: number): NodeKey {
    const info = this.pistes.get(pisteId);
    if (!info) return pisteNodeKey(pisteId, s);
    if (s >= info.length - 0.5) return info.to;
    const at = r0(s);
    for (const st of info.stops) if (st.s === at) return st.node;
    return this.canonical(pisteNodeKey(pisteId, s));
  }

  /** Position au sol d'un nœud (village par défaut). */
  nodePos(key: NodeKey): Vec2 {
    const k = this.canonical(key);
    const n = this.nodes.get(k);
    if (n) return n.pos;
    const at = parsePisteNode(k);
    const host = at ? this.pistes.get(at.piste) : undefined;
    if (at && host) return trackPoint(host.track, at.s);
    const v = this.map.features.village;
    return [v.x, v.z];
  }

  label(key: NodeKey): string {
    const k = this.canonical(key);
    const n = this.nodes.get(k);
    if (n) return n.label;
    return k[0] === 'P' ? 'Hors réseau' : 'Village';
  }

  /** Vrai si un skieur placé en `from` peut rejoindre `to` à pied. */
  canWalk(from: Vec2, to: Vec2, maxDist: number = NET.walk): boolean {
    if (Math.hypot(to[0] - from[0], to[1] - from[1]) > maxDist) return false;
    return this.hf.height(to[0], to[1]) - this.hf.height(from[0], from[1]) <= NET.walkClimb;
  }

  /** Gares aval atteignables à pied depuis un nœud (enchaînement limité depuis un sommet). */
  walkLinks(key: NodeKey): NodeKey[] {
    const k = this.canonical(key);
    const pos = this.nodePos(k);
    const max = k[0] === 'T' ? NET.walkFromTop : NET.walk;
    const out: NodeKey[] = [];
    for (const { lift, entry } of this.lifts.values()) {
      const b = `B${lift.id}`;
      if (b !== k && this.canWalk(pos, entry, max)) out.push(b);
    }
    const v = this.map.features.village;
    if (k !== 'V' && k[0] !== 'T' && Math.hypot(pos[0] - v.x, pos[1] - v.z) < this.map.villageZone) out.push('V');
    return out;
  }

  /** À appeler quand l'ouverture d'une piste change (les valeurs en dépendent). */
  invalidateValues(): void {
    this.valueCache.clear();
  }

  private homeCache: Map<NodeKey, number> | null = null;

  /**
   * Temps estimé (s) pour rentrer au village depuis un nœud en enchaînant
   * descentes, remontées et marche : sur une grande carte, un skieur parti
   * sur un versant arrière reprend une remontée pour revenir. Infinity :
   * aucun chemin. Calculé une fois par réseau (Dijkstra depuis le village
   * sur le graphe inversé) ; les pistes fermées comptent (on redescend quand
   * même), l'état des remontées se vérifie au moment d'embarquer.
   */
  homeCost(node: NodeKey): number {
    if (!this.homeCache) this.homeCache = this.computeHomeCosts();
    return this.homeCache.get(this.canonical(node)) ?? Infinity;
  }

  private computeHomeCosts(): Map<NodeKey, number> {
    // Arêtes inversées : pour chaque nœud, d'où l'on y arrive et en combien de temps.
    const into = new Map<NodeKey, { from: NodeKey; cost: number }[]>();
    const link = (from: NodeKey, to: NodeKey, cost: number) => {
      const a = this.canonical(from);
      const b = this.canonical(to);
      if (a === b) return;
      const list = into.get(b);
      if (list) list.push({ from: a, cost });
      else into.set(b, [{ from: a, cost }]);
    };
    const seen = new Set<NodeKey>();
    const queue: NodeKey[] = ['V', ...this.byNode.keys()];
    for (const { lift } of this.lifts.values()) queue.push(`B${lift.id}`, `T${lift.id}`);
    while (queue.length) {
      const key = this.canonical(queue.pop() as NodeKey);
      if (seen.has(key)) continue;
      seen.add(key);
      for (const o of this.options(key)) {
        const next = this.exits(o.piste, o.s0)[0];
        if (!next) continue;
        link(key, next.node, Math.max(1, next.s - o.s0) / HOME.skiSpeed);
        queue.push(next.node);
      }
      if (key[0] === 'B') {
        const l = this.lifts.get(Number(key.slice(1)));
        if (l && l.lift.status !== 'construction') {
          link(key, `T${l.lift.id}`, l.geo.length / LIFTS[l.lift.kind].speed + HOME.liftWait);
          queue.push(`T${l.lift.id}`);
        }
      }
      const pos = this.nodePos(key);
      for (const w of this.walkLinks(key)) {
        const to = this.nodePos(w);
        link(key, w, Math.hypot(to[0] - pos[0], to[1] - pos[1]) / WALK_SPEED);
        queue.push(w);
      }
    }
    // Dijkstra depuis le village : quelques centaines de nœuds, une file non triée suffit.
    const dist = new Map<NodeKey, number>([['V', 0]]);
    const open: NodeKey[] = ['V'];
    const done = new Set<NodeKey>();
    while (open.length) {
      let bi = 0;
      for (let i = 1; i < open.length; i++) if ((dist.get(open[i] as NodeKey) as number) < (dist.get(open[bi] as NodeKey) as number)) bi = i;
      const key = open.splice(bi, 1)[0] as NodeKey;
      if (done.has(key)) continue;
      done.add(key);
      const d = dist.get(key) as number;
      for (const e of into.get(key) ?? []) {
        const nd = d + e.cost;
        if (nd < (dist.get(e.from) ?? Infinity)) {
          dist.set(e.from, nd);
          open.push(e.from);
        }
      }
    }
    return dist;
  }

  /**
   * Intérêt d'une descente depuis chaque nœud pour un niveau (0..1) : la
   * meilleure suite de pistes jusqu'à une gare aval ou au village, notée sur
   * sa pire portion (un débutant fuit une bleue qui finit en rouge). Une
   * descente qui finit dans un cul-de-sac vaut peu.
   */
  value(node: NodeKey, skill: number, openOnly = true): number {
    const cacheKey = `${skill}:${openOnly ? 1 : 0}`;
    let memo = this.valueCache.get(cacheKey);
    if (!memo) {
      memo = new Map();
      this.valueCache.set(cacheKey, memo);
    }
    return this.skiValue(this.canonical(node), skill, openOnly, memo, new Set());
  }

  private skiValue(key: NodeKey, skill: number, openOnly: boolean, memo: Map<NodeKey, number>, stack: Set<NodeKey>): number {
    const known = memo.get(key);
    if (known !== undefined) return known;
    if (key === 'V' || (key[0] === 'B' && this.lifts.has(Number(key.slice(1))))) {
      memo.set(key, 1);
      return 1;
    }
    if (stack.has(key)) return 0;
    stack.add(key);
    let best = -1;
    for (const o of this.options(key)) {
      const info = this.pistes.get(o.piste);
      if (!info || (openOnly && info.piste.open === false)) continue;
      const su = pisteSuitability(skill, info.piste.difficulty);
      // La prochaine sortie suffit : au raccord suivant, poursuivre la piste est une option.
      const next = this.exits(o.piste, o.s0)[0];
      if (!next) continue;
      const v = Math.min(su, this.skiValue(this.canonical(next.node), skill, openOnly, memo, stack));
      if (v > best) best = v;
    }
    stack.delete(key);
    const res = best >= 0 ? best : this.terminalValue(key);
    memo.set(key, res);
    return res;
  }

  /** Nœud sans descente : bien placé s'il touche une gare ou le village, sinon cul-de-sac. */
  private terminalValue(key: NodeKey): number {
    if (key[0] === 'T') return 0.1;
    return this.walkLinks(key).length ? 0.9 : 0.3;
  }

  /** Tout ce qu'on atteint depuis un nœud (pistes, remontées, nœuds). */
  reach(start: NodeKey, opts: ReachOptions = {}): Reach {
    const maxRank = opts.skill === undefined ? 3 : (SKILL_LEVELS[opts.skill]?.max ?? 3);
    const useLifts = opts.lifts !== false;
    const walk = opts.walk !== false;
    const out: Reach = { nodes: new Set(), pistes: new Map(), lifts: new Set() };
    const queue: NodeKey[] = [this.canonical(start)];
    while (queue.length) {
      const key = queue.pop() as NodeKey;
      if (out.nodes.has(key)) continue;
      out.nodes.add(key);
      for (const o of this.options(key)) {
        const info = this.pistes.get(o.piste);
        if (!info) continue;
        if (opts.openOnly && info.piste.open === false) continue;
        if (DIFFICULTY_RANK[info.piste.difficulty] > maxRank) continue;
        const known = out.pistes.get(o.piste);
        if (known === undefined || o.s0 < known) out.pistes.set(o.piste, o.s0);
        for (const e of this.exits(o.piste, o.s0)) queue.push(this.canonical(e.node));
      }
      if (useLifts && key[0] === 'B') {
        const id = Number(key.slice(1));
        const l = this.lifts.get(id);
        if (l && l.lift.status !== 'construction' && !(opts.openOnly && (l.lift.status === 'broken' || l.lift.status === 'closed'))) {
          out.lifts.add(id);
          queue.push(`T${id}`);
        }
      }
      if (walk) for (const w of this.walkLinks(key)) queue.push(w);
    }
    return out;
  }

  /** Défauts du réseau : culs-de-sac, départs isolés, sommets sans descente, gares inaccessibles. */
  issues(): NetworkIssue[] {
    const out: NetworkIssue[] = [];
    for (const info of this.pistes.values()) {
      const name = info.piste.name;
      if (parsePisteNode(info.from)?.piste === info.piste.id) {
        out.push({ kind: 'isolated-start', level: 'warn', pisteId: info.piste.id, title: `${name} : départ isolé`, message: 'Son départ ne touche ni remontée ni piste : seuls les marcheurs la trouvent.' });
      }
      const end = parsePisteNode(info.to);
      if (end?.piste === info.piste.id && !this.walkLinks(info.to).length) {
        out.push({ kind: 'dead-end', level: 'warn', pisteId: info.piste.id, title: `${name} : cul-de-sac`, message: 'Son arrivée ne rejoint ni gare, ni piste, ni village.' });
      }
    }
    const fromVillage = this.reach('V');
    for (const { lift } of this.lifts.values()) {
      if (!this.options(`T${lift.id}`).length) {
        out.push({ kind: 'no-descent', level: 'warn', liftId: lift.id, title: `${lift.name} : aucune descente`, message: 'Aucune piste ne part de son sommet.' });
      } else if (lift.status !== 'construction' && this.value(`T${lift.id}`, 0, false) < 0.4) {
        out.push({ kind: 'experts-only', level: 'info', liftId: lift.id, title: `${lift.name} : sommet difficile`, message: 'Aucune descente verte ou bleue de bout en bout : les débutants l\u2019évitent.' });
      }
      if (!fromVillage.nodes.has(`B${lift.id}`)) {
        out.push({ kind: 'unreachable', level: 'warn', liftId: lift.id, title: `${lift.name} : hors d'atteinte`, message: 'Aucun chemin depuis le village ne mène à sa gare aval.' });
      }
    }
    return out;
  }
}

/** Extrémités raccordées de chaque piste (départ, arrivée), déduites de la géométrie. */
export function resolvePisteEnds(src: NetworkSource): Map<EntityId, { from: NodeKey; to: NodeKey }> {
  const net = new PisteNetwork(src);
  const out = new Map<EntityId, { from: NodeKey; to: NodeKey }>();
  for (const [id, info] of net.pistes) out.set(id, { from: info.from, to: info.to });
  return out;
}

/** Distance d'un point au tracé d'une piste (pour l'attache des raccords à l'édition). */
export function distToPiste(path: readonly Vec2[], p: Vec2): number {
  return distPointPolyline(p, path);
}
